import React, {useEffect, useRef, useState} from "react";
import {removeItem, insertItem, toggleCollapseAtPath, getDepthFromPath} from "./NestableUtils"
import { NestableItem } from "./NestableItem";
import "./Nestable.scoped.scss"
import { StepTypes } from "interfaces/sw/SWInterfaces";
 
type DragState = {
  path: number[];
  position: "before" | "after" | "inside";
  height?: number;
}
 
type Props = {
  items: Item[];
  renderItem: (params: {
    item: Item;
    depth: number;
    collapseIcon: React.ReactNode;
  }) => React.ReactNode;
  onChange?: (params: {
    items: Item[];
    dragItem: Item;
    targetPath: number[];
  }) => boolean;
  childrenProps?: string;
  maxDepth?: number;
  renderCollapseIcon?: any;
  displayAll?: boolean;
  isStepList?: boolean;
}
 
export type Item = {
    id: string;
    text: string;
    children?: Item[];
    nestedIn?: string | null;
    link?: string | null;
    type?: number;
    parentIndex?: string;
    parentGuid?: string | null;
    parentId?: number | null;
    sortOrder?: number;
    stepId?: number;
    isConditional?: boolean;
    hasUnResolvedComments?: boolean;
    isTask?: boolean;
    stepType?: StepTypes,
    isInTask?: boolean;
    isRCTask?: boolean;
    rcContainer?: boolean;
    hasMasterStep?: boolean;
    isRCStep?: boolean;
    stepNumber?: string;
    checked?: boolean;
    depth?: number;
    isEnabled?: boolean;
    collapsed?: boolean;
    [key: string]: any;
  };
 
 
  export const Nestable: React.FC<Props> = ({
    items: initialItems,
    renderItem,
    onChange,
    renderCollapseIcon,
    childrenProps = "children",
    maxDepth = 3,
    displayAll = false,
    isStepList = true
  }) => {
    const [items, setItems] = useState<Item[]>(initialItems);
    const [dragState, setDragState] = useState<DragState | null>(null);
    const dragRef = useRef<{item: Item; path: number[], height: number} | null>(null);
 
    const normalizePathAfterRemoval = (
      path: number[],
      removedPath: number[]
    ): number[] => {
      const normalized = [...path];
      const max = Math.min(normalized.length, removedPath.length);
 
      let divergeAt = 0;
      while (divergeAt < max && normalized[divergeAt] === removedPath[divergeAt]) {
        divergeAt += 1;
      }
 
      // Only shift the index when the removed item came from the same siblings list.
      // If removal happened deeper in a subtree, parent-level indexes do not change.
      if (
        divergeAt < max &&
        removedPath.length - 1 === divergeAt &&
        normalized[divergeAt] > removedPath[divergeAt]
      ) {
        normalized[divergeAt] -= 1;
      }
 
      return normalized;
    }
 
    const getFinalPath = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) : number[] => {
      const normalizedTargetPath = normalizePathAfterRemoval(targetPath, dragPath);
 
      if(position === "inside"){
        return [...normalizedTargetPath, 0]
      }
 
      const targetParent = normalizedTargetPath.slice(0, -1)
      const targetIndex = normalizedTargetPath[normalizedTargetPath.length - 1];
 
        return [
          ...targetParent,
          position === "after" ? targetIndex + 1 : targetIndex
        ]
    }
 
    const isSamePosition = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) => {
      const finalPath = getFinalPath(dragPath, targetPath, position)
      return finalPath.join(",") === dragPath.join(",")
    }
 
    const canDrop = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) => {
      return isDropAllowed(dragPath, targetPath, position);
    }
 
    useEffect(() => {
      setItems(initialItems);
    }, [initialItems])
 
    const isDropAllowed = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) => {
      const targetDepth = getDepthFromPath(targetPath);
 
      if(targetPath.join(",").startsWith(dragPath.join(","))) return false;
 
      const finalDepth = position === "inside" ? targetDepth + 1 : targetDepth;
 
      if(finalDepth >= maxDepth) return false;
 
      if(isSamePosition(dragPath, targetPath, position)) return false;
 
      return true;
    }
 
    const handleToggle = (path: number[]) => {
      setItems(prevItems => { return toggleCollapseAtPath(prevItems, path, childrenProps)});
   
    }
 
    const handleDrop = (dragPath: number[], targetPath: number[], position: DragState["position"]) => {
      if(!dragRef.current) return;
 
      if(!isDropAllowed(dragPath, targetPath, position)){
        setDragState(null);
        return;
      }
 
      const {list: afterRemove, removed} = removeItem(items, dragPath, childrenProps);
 
      let finalPath: number[] = getFinalPath(dragPath, targetPath, position);
 
      const newItems = insertItem(
        afterRemove,
        finalPath,
        removed,
        childrenProps
      )
      setDragState(null);
      dragRef.current = null;
 
      const payload = {
        items: newItems,
        dragItem: removed,
        targetPath: finalPath
      }
 
      if(onChange){
        let result: boolean = onChange(payload);
        if(result){
          setItems(newItems)
        }
       
      }
    }
 
    const handleGlobalDragEnd = () => {
      if(dragRef.current && dragState){
        handleDrop(
          dragRef.current.path,
          dragState.path,
          dragState.position
        );
      } else {
        setDragState(null);
        dragRef.current = null;
      }
    }
 
    return (
      <div className="nestable">
        <ul className="nestable-list">
          {items.map((item, i) => (
            <NestableItem
              key={item.id}
              item={item}
              depth={0}
              path={[i]}
              renderItem={renderItem}
              childrenProps={childrenProps}
              dragState={dragState}
              displayAll={displayAll}
              setDragState={(state) => {
                if(!dragRef.current) return;
 
                if(
                  state &&
                  !canDrop(dragRef.current.path,
                    state.path,
                    state.position
                  )
                ){
                  return;
                }
                setDragState(state)
              }}
              onToggle={handleToggle}
              onDragStart={(data: any) => {
                dragRef.current = data;
                setDragState({
                  path: data.path,
                  position: "inside",
                  height: data.height
                })
              }}
              onDragEnd={handleGlobalDragEnd}
              isStepList={isStepList}
              renderCollapseIcon={renderCollapseIcon}
              onDrop={(targetPath: any, position: any) => {
                if(dragRef.current){
                  handleDrop(
                    dragRef.current.path,
                    targetPath,
                    position
                  )
                }
              }}
              />
          ))}
        </ul>
      </div>
    )
  }